"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.IkeRest = exports.REQUIRED_KEYS = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const config_1 = require("../config");
const util_1 = require("../util");
const fetchable_1 = require("./fetchable");
const rest_error_1 = require("./rest.error");
const rest_user_1 = require("./rest.user");
exports.REQUIRED_KEYS = [
    'application.name',
    'application.url',
    'application.version'
];
const HEADER_CONTENT_JSON = 'application/json';
class IkeRest {
    constructor(log, applicationName, baseUrl, applicationVersion, rayId) {
        this.errorListeners = {};
        this.Log = {
            create: this.bindAction('log.create')
        };
        this.Capture = {
            thumbnail: this.bindAction('capture.thumbnail'),
            composite: this.bindAction('collection.capture.composite'),
            measurement: this.bindAction('collection.capture.measurement'),
            brightness: this.bindAction('collection.capture.brightness'),
            crop: this.bindAction('collection.capture.crop')
        };
        this.Verify = {
            check: this.bindAction('verify.check'),
            checkEmail: this.bindAction('verify.check.email'),
            forceAcceptInvite: this.bindAction('verify.force.accept.invite')
        };
        this.User = {
            create: this.bindAction('user.create')
        };
        this.SSOUser = {
            create: this.bindAction('sso.user.create'),
            triggerResetPassword: this.bindAction('sso.reset.password'),
            selfMigrate: this.bindAction('sso.user.migrate')
        };
        this.Version = {
            check: this.bindAction('version.check'),
            data: this.bindAction('version.data')
        };
        this.appName = applicationName;
        this.baseUrl = baseUrl;
        this.version = applicationVersion;
        this.fetch = config_1.IkeConfigStore.fetch;
        this.log = log;
        this.requester = `${this.appName}-${this.version}`;
        this.rayId = rayId;
    }
    static create(config, opts) {
        return new IkeRest(opts.log, config.mustGetString('application.name'), config.getString('application.url.internal') || config.mustGetString('application.url'), config.mustGetString('application.version'), opts.rayId);
    }
    static createStore() {
        return new IkeRest.FetchableStore();
    }
    bind(token) {
        return new rest_user_1.IkeRestUser(this, token);
    }
    bindAction(action) {
        return (data) => this.action(action, data);
    }
    authenticate(sso_id, token) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const data = { sso_id, token };
            const profile = yield this.action('authenticate', data, token);
            const userRest = this.bind(token);
            userRest.me.init(profile);
            return userRest;
        });
    }
    onError(type, callback) {
        if (this.errorListeners[type] == null) {
            this.errorListeners[type] = [callback];
        }
        else {
            this.errorListeners[type].push(callback);
        }
    }
    action(path, data, sessionToken = null, departmentId = null) {
        return this._post(path, data, sessionToken, departmentId);
    }
    actionRaw(path, data, sessionToken = null, departmentId = null) {
        return this._raw(path, data, sessionToken, departmentId);
    }
    headers(sessionToken, departmentId) {
        const headers = {};
        headers[core_api_1.IkeRequestHeader.REQUESTER] = this.requester;
        headers[core_api_1.IkeRequestHeader.CONTENT_TYPE] = HEADER_CONTENT_JSON;
        if (sessionToken != null) {
            // TODO: Remove JWT check and bearer prefix check by ensuring sessionToken is correct elsewhere
            if ((0, util_1.isJWT)(sessionToken)) {
                // If this already has the bearer prefix, don't append it again
                let authHeader = /^Bearer .*/.test(sessionToken)
                    ? sessionToken
                    : `Bearer ${sessionToken}`;
                headers[core_api_1.IkeRequestHeader.AUTHORIZATION] = authHeader;
            }
            else {
                headers[core_api_1.IkeRequestHeader.AUTHORIZATION] = `token ${sessionToken}`;
            }
        }
        if (this.rayId) {
            headers[core_api_1.IkeRequestHeader.RAY_ID] = this.rayId;
        }
        if (departmentId) {
            headers[core_api_1.IkeRequestHeader.DEPARTMENT] = departmentId;
        }
        return headers;
    }
    path(path) {
        if (path.indexOf('/') === -1) {
            return this.baseUrl + '/v1/action/' + path;
        }
        return this.baseUrl + path;
    }
    _raw(path, data, sessionToken, departmentId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const body = data || {};
            const method = 'POST'; // IF this ever changes, need to remove BODY from request.
            const headers = this.headers(sessionToken, departmentId);
            const apiUrl = this.path(path);
            const startTime = Date.now();
            this.log.trace({ baseUrl: this.baseUrl }, `CallStart - ${path}`);
            const response = yield this.fetch(apiUrl, { method, headers, body: JSON.stringify(body) });
            const duration = Date.now() - startTime;
            const responseId = response.headers.get(core_api_1.IkeRequestHeader.REQUEST_ID);
            if (response.ok) {
                this.log.info({ responseId, duration, status: response.status }, `CallDone - ${path}`);
            }
            else {
                this.log.warn({ responseId, duration, status: response.status }, `CallDone - ${path}`);
            }
            return response;
        });
    }
    handleError(response) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            yield this.validateIsJson(response);
            const json = yield response.json();
            const requestError = new rest_error_1.IkeRequestError(response.status, json);
            const requestErrorType = rest_error_1.IkeRequestErrorChecker.getErrorType(requestError);
            const errorListeners = this.errorListeners[requestErrorType] || [];
            for (const listener of errorListeners) {
                listener(requestError);
            }
            throw new rest_error_1.IkeRequestError(response.status, json);
        });
    }
    validateIsJson(response) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const contentType = response.headers.get(core_api_1.IkeRequestHeader.CONTENT_TYPE) || '';
            if (contentType.indexOf(HEADER_CONTENT_JSON) === -1) {
                const text = yield response.text();
                throw new rest_error_1.IkeRequestError(response.status, { message: text });
            }
        });
    }
    _post(path, data, sessionToken, departmentId) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const response = yield this._raw(path, data, sessionToken, departmentId);
            if (response.ok) {
                yield this.validateIsJson(response);
                const res = yield response.json();
                return res;
            }
            return this.handleError(response);
        });
    }
}
exports.IkeRest = IkeRest;
IkeRest.FetchableStore = fetchable_1.IkeFetchableStore;
//# sourceMappingURL=rest.js.map